"use client"
import { useEffect, useState, useRef, useMemo } from "react"
import { motion } from "framer-motion"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useLanguage } from "@/lib/i18n"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

// ============================================================================
// TRANSFORMATION DATA - 3 Core Bridges
// ============================================================================

interface GapCard {
  id: string
  without: string
  with: string
  description: string
  color: string
  chaosPattern: string
  formPattern: string
}

// Site-aligned colors using CSS variables
const gapCards: GapCard[] = [
  {
    id: "complexity-story",
    without: "Complexity",
    with: "Story",
    description: "Brilliance that can't be told can't be sold.",
    color: "var(--color-accent-teal)",
    chaosPattern: "cluster-overlap",
    formPattern: "bubble",
  },
  {
    id: "vision-strategy",
    without: "Vision",
    with: "Strategy",
    description: "Dreams without direction are just daydreams.",
    color: "var(--color-accent-gold)",
    chaosPattern: "blur-scatter",
    formPattern: "arrow",
  },
  {
    id: "tech-humanity",
    without: "Technology",
    with: "Humanity",
    description: "Innovation that can't connect stays invisible.",
    color: "var(--color-accent-copper)",
    chaosPattern: "grid-scatter",
    formPattern: "heart",
  },
]

// ============================================================================
// PARTICLE POSITION GENERATORS
// ============================================================================

function getChaosPosition(pattern: string, index: number, total: number) {
  const t = index / total

  switch (pattern) {
    case "grid-scatter":
      const gridX = (index % 8) * 10 + 15
      const gridY = Math.floor(index / 8) * 10 + 20
      return {
        x: gridX + (Math.random() - 0.5) * 30,
        y: gridY + (Math.random() - 0.5) * 30,
      }
    case "cluster-overlap":
      const clusterIndex = index % 4
      const clusterCenters = [
        { x: 30, y: 30 },
        { x: 70, y: 30 },
        { x: 30, y: 70 },
        { x: 70, y: 70 },
      ]
      const center = clusterCenters[clusterIndex]
      return {
        x: center.x + (Math.random() - 0.5) * 40,
        y: center.y + (Math.random() - 0.5) * 40,
      }
    case "blur-scatter":
      const blurAngle = t * Math.PI * 2
      const blurDist = 15 + Math.random() * 35
      return {
        x: 50 + Math.cos(blurAngle) * blurDist + (Math.random() - 0.5) * 20,
        y: 50 + Math.sin(blurAngle) * blurDist + (Math.random() - 0.5) * 20,
      }
    default:
      return { x: Math.random() * 100, y: Math.random() * 100 }
  }
}

function getFormPosition(pattern: string, index: number, total: number) {
  const t = index / total
  const angle = t * Math.PI * 2

  switch (pattern) {
    case "heart":
      // Heart shape - raised higher (y offset adjusted from 45 to 40)
      const heartT = t * Math.PI * 2
      return {
        x: 50 + 16 * Math.pow(Math.sin(heartT), 3) * 1.5,
        y:
          40 -
          (13 * Math.cos(heartT) -
            5 * Math.cos(2 * heartT) -
            2 * Math.cos(3 * heartT) -
            Math.cos(4 * heartT)) *
            1.2,
      }
    case "arrow":
      const arrowPoints = [
        ...Array(20)
          .fill(0)
          .map((_, i) => ({ x: 25 + i * 2, y: 50 })),
        ...Array(10)
          .fill(0)
          .map((_, i) => ({ x: 65 - i * 2, y: 50 - i * 2 })),
        ...Array(10)
          .fill(0)
          .map((_, i) => ({ x: 65 - i * 2, y: 50 + i * 2 })),
      ]
      return arrowPoints[index % arrowPoints.length]
    case "bubble":
      if (t < 0.8) {
        const bubbleAngle = (t / 0.8) * Math.PI * 2
        return {
          x: 50 + Math.cos(bubbleAngle) * 25 * (1 + 0.3 * Math.cos(bubbleAngle * 2)),
          y: 45 + Math.sin(bubbleAngle) * 20,
        }
      } else {
        const tailT = (t - 0.8) / 0.2
        return {
          x: 35 - tailT * 10,
          y: 65 + tailT * 15,
        }
      }
    default:
      return { x: 50 + Math.cos(angle) * 25, y: 50 + Math.sin(angle) * 25 }
  }
}

// ============================================================================
// PARTICLE FIELD COMPONENT
// ============================================================================

interface Particle {
  id: number
  chaosX: number
  chaosY: number
  formX: number
  formY: number
}

type Phase = "intro" | "chaos" | "transition" | "form"

function ParticleField({
  card,
  phase,
  isVisible,
}: {
  card: GapCard
  phase: Phase
  isVisible: boolean
}) {
  const particles = useMemo(() => {
    const total = 50
    return Array(total)
      .fill(0)
      .map((_, i) => {
        const chaos = getChaosPosition(card.chaosPattern, i, total)
        const form = getFormPosition(card.formPattern, i, total)
        return {
          id: i,
          chaosX: chaos.x,
          chaosY: chaos.y,
          formX: form.x,
          formY: form.y,
        }
      })
  }, [card.chaosPattern, card.formPattern])

  if (!isVisible) return null

  return (
    <div className="absolute inset-0 overflow-hidden">
      {particles.map((p) => {
        let x: number, y: number, scale: number, opacity: number

        switch (phase) {
          case "intro":
            x = 50
            y = 50
            scale = 0
            opacity = 0
            break
          case "chaos":
            x = p.chaosX
            y = p.chaosY
            scale = 0.8 + Math.random() * 0.4
            opacity = 0.4 + Math.random() * 0.3
            break
          case "transition":
            x = (p.chaosX + p.formX) / 2
            y = (p.chaosY + p.formY) / 2
            scale = 1
            opacity = 0.6
            break
          case "form":
            x = p.formX
            y = p.formY
            scale = 1
            opacity = 0.85
            break
        }

        return (
          <motion.div
            key={p.id}
            className="absolute rounded-full"
            style={{
              width: 5,
              height: 5,
              backgroundColor: card.color,
              boxShadow: `0 0 10px ${card.color}`,
            }}
            animate={{
              left: `${x}%`,
              top: `${y}%`,
              scale,
              opacity,
            }}
            transition={{
              // Faster durations
              duration: phase === "intro" ? 0.4 : phase === "chaos" ? 1 : 1.2,
              ease: phase === "form" ? "easeOut" : "easeInOut",
              delay: phase === "chaos" ? p.id * 0.004 : phase === "form" ? p.id * 0.006 : 0,
            }}
          />
        )
      })}
    </div>
  )
}

// ============================================================================
// SINGLE TRANSFORMATION PANEL (for 3-column layout)
// ============================================================================

function TransformationPanel({
  card,
  index,
  isVisible,
  locale,
}: {
  locale: string
  card: GapCard
  index: number
  isVisible: boolean
}) {
  const [phase, setPhase] = useState<Phase>("intro")
  const [isInView, setIsInView] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Intersection observer for panel visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting)
      },
      { threshold: 0.3 }
    )

    if (panelRef.current) {
      observer.observe(panelRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Animation cycle when panel is in view - FASTER timing
  useEffect(() => {
    if (!isInView || !isVisible) {
      setPhase("intro")
      return
    }

    setPhase("intro")

    // Faster animation timeline
    const timeline = [
      { phase: "chaos" as const, delay: 400 },
      { phase: "transition" as const, delay: 1800 },
      { phase: "form" as const, delay: 2800 },
      { phase: "intro" as const, delay: 5000 },
    ]

    const timeouts = timeline.map(({ phase: p, delay }) => setTimeout(() => setPhase(p), delay))

    // Faster loop cycle
    const loopInterval = setInterval(() => {
      setPhase("intro")
      timeline.forEach(({ phase: p, delay }) => {
        setTimeout(() => setPhase(p), delay)
      })
    }, 5500)

    return () => {
      timeouts.forEach(clearTimeout)
      clearInterval(loopInterval)
    }
  }, [isInView, isVisible])

  return (
    <motion.div
      ref={panelRef}
      className="relative flex flex-col items-center"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 40 }}
      transition={{ duration: 0.8, delay: 0.2 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Without State Label */}
      <motion.p
        className="text-xl md:text-2xl lg:text-3xl font-semibold text-[var(--color-text-secondary)] mb-4"
        style={{
          fontFamily: "var(--font-display)",
        }}
        animate={{
          opacity: phase === "chaos" || phase === "intro" ? 0.8 : 0.35,
        }}
        transition={{ duration: 0.5 }}
      >
        {card.without}
      </motion.p>

      {/* Particle Animation Field */}
      <div className="relative w-full aspect-square max-w-[220px] md:max-w-[260px] lg:max-w-[300px] flex items-center justify-center">
        {/* Subtle background glow */}
        <motion.div
          className="absolute inset-0 rounded-2xl"
          animate={{
            background:
              phase === "form"
                ? `radial-gradient(circle at 50% 50%, ${card.color}20 0%, transparent 70%)`
                : "transparent",
          }}
          transition={{ duration: 0.6 }}
        />

        {/* Particle field */}
        <ParticleField card={card} phase={phase} isVisible={isInView && isVisible} />
      </div>

      {/* With State Label */}
      <motion.p
        className="text-2xl md:text-3xl lg:text-4xl font-semibold mt-4"
        style={{
          fontFamily: "var(--font-display)",
          color: card.color,
        }}
        animate={{
          opacity: phase === "form" ? 1 : 0.35,
          textShadow: phase === "form" ? `0 0 30px ${card.color}50` : "none",
        }}
        transition={{ duration: 0.5 }}
      >
        {card.with}
      </motion.p>

      {/* Description */}
      <motion.p
        className={`text-center text-sm md:text-base lg:text-lg text-[var(--color-text-secondary)] mt-4 max-w-[250px]${locale !== "zh" ? " italic" : " font-light"}`}
        animate={{ opacity: phase === "form" ? 0.9 : 0.5 }}
        transition={{ duration: 0.4 }}
      >
        "{card.description}"
      </motion.p>
    </motion.div>
  )
}

// ============================================================================
// MAIN SECTION COMPONENT
// ============================================================================

export function TranslationTypesSection() {
  const { t, tArray, locale } = useLanguage()
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const headlineRef = useRef<HTMLHeadingElement>(null)

  // Create locale-aware gap cards
  const localizedGapCards = gapCards.map((card) => ({
    ...card,
    without:
      card.id === "complexity-story"
        ? t("gap.cards.complexity.without")
        : card.id === "vision-strategy"
          ? t("gap.cards.vision.without")
          : t("gap.cards.tech.without"),
    with:
      card.id === "complexity-story"
        ? t("gap.cards.complexity.with")
        : card.id === "vision-strategy"
          ? t("gap.cards.vision.with")
          : t("gap.cards.tech.with"),
    description:
      card.id === "complexity-story"
        ? t("gap.cards.complexity.description")
        : card.id === "vision-strategy"
          ? t("gap.cards.vision.description")
          : t("gap.cards.tech.description"),
  }))

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1, rootMargin: "-50px" }
    )

    const observedElement = sectionRef.current
    if (observedElement) {
      observer.observe(observedElement)
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement)
      }
    }
  }, [])

  // GSAP text split animation for headline
  useEffect(() => {
    if (!headlineRef.current) return

    const words = headlineRef.current.querySelectorAll(".headline-word")
    if (words.length === 0) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        words,
        {
          opacity: 0,
          y: 80,
          rotateX: -60,
          filter: "blur(10px)",
        },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          filter: "blur(0px)",
          duration: 0.9,
          ease: "power4.out",
          stagger: 0.08,
          scrollTrigger: {
            trigger: headlineRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      )
    })

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="services"
      data-section="work"
      className="py-24 md:py-32 px-6 md:px-8 lg:px-12 relative overflow-hidden bg-[var(--color-bg-primary)]"
    >
      {/* Subtle ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse 80% 50% at 50% 50%, var(--color-accent-gold)08 0%, transparent 70%)`,
          }}
        />
      </div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        {/* Centered Header Block */}
        <div className="mb-16 md:mb-20 text-center">
          <motion.p
            className={`text-2xl md:text-3xl mb-4 text-[var(--color-accent-gold)]${locale !== "zh" ? " rotate-[-2deg]" : ""}`}
            style={{
              fontFamily: "var(--font-handwritten)",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ duration: 0.6 }}
          >
            {t("gap.sectionLabel")}
          </motion.p>

          <h2
            ref={headlineRef}
            className="text-4xl md:text-5xl lg:text-6xl font-medium mb-8 text-[var(--color-text-primary)] perspective-[1000px]"
            style={{
              fontFamily: "var(--font-display)",
            }}
          >
            {tArray("gap.headline.words").map((word, i) => (
              <span
                key={i}
                className="headline-word inline-block mx-2"
                style={{ transformStyle: "preserve-3d" }}
              >
                {word}
              </span>
            ))}
          </h2>

          <motion.div
            className="text-lg md:text-xl lg:text-2xl max-w-[700px] mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible ? 0.8 : 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ lineHeight: 1.6 }}
          >
            <p className="font-medium text-[var(--color-text-secondary)]">{t("gap.subtitle1")}</p>
            <p className="mt-4 text-base md:text-lg text-[var(--color-text-muted)]">
              {t("gap.subtitle2")}
            </p>
          </motion.div>
        </div>

        {/* 3-Column Transformation Panels - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-12">
          {localizedGapCards.map((card, index) => (
            <TransformationPanel
              key={card.id}
              card={card}
              index={index}
              isVisible={isVisible}
              locale={locale}
            />
          ))}
        </div>

        {/* Bridge Statement */}
        <motion.div
          className="mt-20 md:mt-28 text-center relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          <motion.p
            className="text-3xl md:text-4xl lg:text-5xl text-[var(--color-accent-teal)] rotate-[-1deg]"
            style={{ fontFamily: "var(--font-handwritten)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            {t("gap.bridge")}
          </motion.p>

          <motion.p
            className="mt-6 text-xl md:text-2xl lg:text-3xl text-[var(--color-text-secondary)] max-w-lg mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible ? 0.8 : 0 }}
            transition={{ duration: 0.6, delay: 1.8 }}
          >
            {t("gap.bridgeSub")}
          </motion.p>
        </motion.div>
      </div>
    </section>
  )
}
